import React, { useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { AtLeastOne } from '~/types';
import { ReactComponent as IconPrefDropdownArrow } from '@svg/common/icon-pref-dropdown-arrow.svg';
// import { ReactComponent as IconSocialTiktok } from '@svg/social/sidebar/tiktok.svg';
import { ReactComponent as IconSocialYoutube } from '@svg/social/sidebar/youtube.svg';
// import { ReactComponent as IconSocialInstagram } from '@svg/social/sidebar/instagram.svg';
// import { ReactComponent as IconSocialFacebook } from '@svg/social/sidebar/facebook.svg';
import { useTranslation } from 'react-i18next';

const SiderSocialLinks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <SocialLinkItem
        id="yt"
        title={t('sider.social.youtube')}
        icon={<IconSocialYoutube />}
        url="https://www.youtube.com/channel/UCitXicTZ1k9KYxSTuv8gCsg"
      />
      {/* <SocialLinkItem
        id="insta"
        title={t('sider.social.instagram')}
        icon={<IconSocialInstagram />}
        lists={[
          {
            title: t('sider.social.children.korea'),
            url: 'https://www.instagram.com/sparky_fit_',
          },
          {
            title: t('sider.social.children.global'),
            url: 'https://www.instagram.com/sparky_global',
          },
        ]}
      /> */}
    </>
  );
};

type SocialLinkItemProps = {
  id: string;
  title: string;
  icon: React.ReactElement;
} & AtLeastOne<SocialLinkItemDataProp>;

interface SocialLinkItemDataProp {
  url: string;
  lists: SocialLinkItemList[];
}

interface SocialLinkItemList {
  title: string;
  url: string;
}

const SocialLinkItem: React.FC<SocialLinkItemProps> = ({ id, title, icon, url, lists }) => {
  const [isListOpen, setListOpen] = useState(false);
  const isList = useMemo(() => lists && lists.length, [lists]);

  const onClickLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isList) {
        e.preventDefault();
        setListOpen(!isListOpen);
      }
    },
    [isList, isListOpen],
  );

  const onKeyPressLink = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (!isList) return;

      switch (e.key) {
        case 'Space':
          setListOpen(!isListOpen);
          break;
        default:
      }
    },
    [isList, isListOpen],
  );

  return (
    <SocialLinkContainer>
      <a
        className="title-area"
        href={isList ? '#' : url}
        target={isList ? undefined : '_blank'}
        role={isList ? 'button' : undefined}
        onClick={onClickLink}
        onKeyPress={onKeyPressLink}
        aria-expanded={isList ? (isListOpen ? 'true' : 'false') : undefined}
        aria-controls={isList ? `a11y_controls_sidebar_social_${id}` : undefined}
        rel="noopener noreferrer"
      >
        <i className="icon">{icon}</i>
        <span className="title">{title}</span>
        {isList && (
          <div className="arrow" aria-hidden="true">
            <IconPrefDropdownArrow width="9" height="5" />
          </div>
        )}
      </a>
      {isList && (
        <ListContainer
          id={`a11y_controls_sidebar_social_${id}`}
          aria-hidden={isListOpen ? 'false' : 'true'}
        >
          {lists!.map((item, index) => (
            <a
              href={item.url}
              target="_blank"
              className="child-link"
              key={`${id}_${index}`}
              rel="noopener noreferrer"
            >
              {item.title}
            </a>
          ))}
        </ListContainer>
      )}
    </SocialLinkContainer>
  );
};

const SocialLinkContainer = styled.div`
  position: relative;

  & + & {
    margin-top: 14px;
  }

  &:hover {
    .title-area {
      text-decoration: underline;

      &[role='button'] {
        text-decoration: none !important;
      }
    }
  }

  .title-area {
    display: block;
    padding: 0 0 0 21px;
    text-decoration: none;
    font-family: 'Noto Sans CJK KR', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: #334d6e;

    &[aria-expanded='true'] {
      & + div {
        display: block;
        max-height: 320px;
      }

      .arrow svg {
        transform: rotate(180deg);
      }
    }
  }

  .icon {
    position: absolute;
    top: 0;
    left: 0;

    svg {
      width: 15px;
      height: 15px;
    }
  }

  .arrow {
    position: absolute;
    top: 0;
    right: 0;
    padding: 6px 0 5px;
    pointer-events: none;

    svg {
      vertical-align: top;
    }
  }
`;

const ListContainer = styled.div`
  display: none;
  overflow: hidden;
  max-height: 0;
  transition: max-height 160ms ease;

  .child-link {
    display: block;
    margin-top: 12px;
    padding-left: 21px;
    font-size: 14px;
    line-height: 16px;
    color: #334d6e;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default SiderSocialLinks;
